// frontend/src/pages/BillingPage/BillingPage.jsx

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
    Box, 
    Typography, 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableRow, 
    Paper, 
    TableContainer, 
    CircularProgress, 
    Button,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
    Drawer,
    IconButton,
    TextField,
    Tooltip,
    Select, 
    MenuItem,
    FormControl,
    InputLabel,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Alert,
    Switch,
    FormControlLabel
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FilterAltIcon from '@mui/icons-material/FilterAlt'; 
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { 
    PieChart, 
    Pie, 
    Cell, 
    ResponsiveContainer, 
    Tooltip as RechartsTooltip,
    Legend,
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid,
    Label
} from 'recharts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './BillingPage.css';
import '../../components/HistoryFilters/HistoryFilters.css'; 

// --- CONSTANTS ---
const ACTIVITY_OPTIONS = [
    'analysis_new', 
    'analysis_cached', 
    'verification_access', 
    'verification_scan_fee', 
    'translation', 
    'content_license', 
    'impression_cpm', 
    'threat_radar_daily', 
    'threat_radar_alert',
    'threat_radar_proactive'
];
const CONTENT_TYPE_OPTIONS = ['text', 'image', 'audio', 'video'];

// --- COLOR MAPPING ---
const COLOR_MAP = {
    // Platform Usage
    'Analysis New': '#0984E3',       // Bright Yellow
    'Analysis Cached': '#FF6B6B',    // Vivid Pink/Red
    'Translation': '#4ECDC4',        // Periwinkle
    'Threat Radar Daily': '#6c5ce7', // (Purple for the daily tracker maintenance)
    'Threat Radar Alert': '#FFFFFF', // (Vivid Red to match the threat theme)
    'Threat Radar Proactive': '#fdcb6e',
    
    // Activation Usage
    'Content License': '#d63031',       // Green (Money/License)
    'Impression Cpm': '#FFE66D',        // Blue (Ads/Views)
    'Verification Access': '#A29BFE',   // Teal (Validation)
    'Verification Scan Fee': '#FF9F43', // Orange (Scans)
    
    // Content Types
    'Image': '#FFE66D',
    'Video': '#FF6B6B',
    'Audio': '#55EFC4',
    'Text': '#74b9ff',
    
    'System': '#b2bec3',
    'Other': '#636e72',
    'N/A': '#636e72'
};


const DEFAULT_COLOR = '#b2bec3';
const PAGE_SIZE = 10;
const COST_UNIT_PRICE_USD = 0.001; 

// --- CATEGORY GROUPING FOR DONUTS ---
const PLATFORM_KEYS = ['Analysis New', 'Analysis Cached', 'Translation', 'Threat Radar Daily', 'Threat Radar Alert'];
const ACTIVATION_KEYS = ['Content License', 'Impression Cpm', 'Verification Access', 'Verification Scan Fee'];

// --- Fixed Order for Legends ---
const ORDERED_KEYS = [
    ...PLATFORM_KEYS,
    ...ACTIVATION_KEYS
];

const sortChartKeys = (a, b) => {
    // 1. Normalize keys to match ORDERED_KEYS format (Trim and Title Case if needed, but usually exact match works best)
    const keyA = a.trim();
    const keyB = b.trim();

    const indexA = ORDERED_KEYS.indexOf(keyA);
    const indexB = ORDERED_KEYS.indexOf(keyB);
    
    // 2. Priority Logic
    if (indexA !== -1 && indexB !== -1) return indexA - indexB; // Both in list: Use list order
    if (indexA !== -1) return -1; // Only A is in list: A comes first
    if (indexB !== -1) return 1;  // Only B is in list: B comes first
    
    // 3. Fallback: Alphabetical for unknown keys (e.g. content types like 'Image', 'Video')
    return keyA.localeCompare(keyB);
};

// --- 1. SYMBOL MAPPING ---
const CURRENCY_SYMBOLS = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'AUD': 'A$',
    'CAD': 'C$',
    'CHF': 'Fr',
    'CNH': '¥'
};

const getColor = (key) => COLOR_MAP[key] || DEFAULT_COLOR;

const DonutTooltip = ({ active, payload, calculateCost, selectedCurrency, formatCurrency, t, translateChartKey, totalUnits }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        // Total of THIS specific chart (passed implicitly via Recharts context, but harder to get here).
        // Easier: Assume parent passed 'totalUnits' or we calculate it? 
        // Recharts payload doesn't easily give total of the whole chart in tooltip.
        
        // However, your previous screenshot showed % relative to the donut.
        // Let's just fix the style first.
        
        const cost = calculateCost(data.value);
        const translatedName = translateChartKey(data.name);
        const percentage = totalUnits > 0 ? ((data.value / totalUnits) * 100).toFixed(1) : 0;

        return (
            <div className="custom-chart-tooltip" style={{ 
                backgroundColor: '#1e293b', 
                border: '1px solid #334155', 
                padding: '12px', 
                borderRadius: '6px', 
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
                textAlign: 'left'
            }}>
                <p style={{ 
                    color: '#ffffff', 
                    fontWeight: 'bold', 
                    margin: '0 0 8px 0', 
                    padding: '0 0 6px 0', 
                    borderBottom: '1px solid #475569', 
                    fontSize: '0.95rem' 
                }}>
                    {translatedName}
                    <span style={{ color: 'var(--secondary-color)', fontSize: '0.8rem', marginLeft: '6px', fontWeight: 'normal' }}>
                        ({percentage}%)
                    </span>
                </p>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <tbody>
                        <tr>
                            <td style={{ color: '#94a3b8', fontSize: '0.8rem', paddingRight: '12px', paddingBottom: '4px' }}>
                                {t('billingPage.charts.units')}:
                            </td>
                            <td style={{ color: '#fff', fontSize: '0.8rem', textAlign: 'right', paddingBottom: '4px' }}>
                                {data.value}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ color: '#94a3b8', fontSize: '0.8rem', paddingRight: '12px' }}>
                                {t('billingPage.charts.cost')}:
                            </td>
                            <td style={{ color: '#66bb6a', fontWeight: 'bold', fontSize: '0.8rem', textAlign: 'right' }}>
                                {formatCurrency(cost, selectedCurrency, 2)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }
    return null;
};

const CustomDonut = React.memo(({ 
    data, title, totalUnits, selectedCurrency, calculateCost, getColor, 
    renderSortedLegend, renderLegendText, formatCurrency, 
    t, translateChartKey, sharePercentage 
}) => {
    const totalCost = calculateCost(totalUnits);
    if (!data || data.length === 0) return null;

    return (
        <div className="donut-wrapper" style={{ width: '100%', height: '300px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', textAlign: 'center', zIndex: 10 }}>
                <Typography variant="subtitle2" component="p" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', textTransform: 'uppercase' }}>
                    {title}
                </Typography>
            </div>
            <ResponsiveContainer>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%" cy="50%"
                        innerRadius={60} outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                        stroke="none"
                    >
                        {data.map((entry, index) => <Cell key={`cell-${index}`} fill={getColor(entry.name)} />)}
                        {/* 1. Main Value Label */}
                        <Label 
                            value={formatCurrency(totalCost, selectedCurrency, 2)} 
                            position="center" 
                            dy={-10} // Move up slightly
                            fill="#ffffff" 
                            style={{ fontSize: '18px', fontWeight: 'bold', textAnchor: 'middle', dominantBaseline: 'middle' }} 
                        />
                        
                        {/* 2. Share Percentage Label (Only if sharePercentage is provided) */}
                        {sharePercentage !== undefined && (
                            <Label 
                                value={`(${Math.round(sharePercentage)}%)`} 
                                position="center" 
                                dy={14} // Move down below the value
                                fill="rgba(255,255,255,0.6)" 
                                style={{ fontSize: '12px', fontWeight: '500', textAnchor: 'middle', dominantBaseline: 'middle' }} 
                            />
                        )}
                    </Pie>
                    {/* USE CUSTOM TOOLTIP WITH PROPS */}
                    <RechartsTooltip 
                        content={
                            <DonutTooltip 
                                calculateCost={calculateCost} 
                                selectedCurrency={selectedCurrency}
                                formatCurrency={formatCurrency}
                                t={t}
                                translateChartKey={translateChartKey}
                                totalUnits={totalUnits}
                            />
                        } 
                        cursor={{ fill: 'transparent' }} 
                    />
                    <Legend 
                        content={renderSortedLegend} 
                        layout="horizontal"     
                        verticalAlign="bottom" 
                        align="center"        
                        wrapperStyle={{ 
                            marginTop: '0px', // Spacing handled by chart positioning now
                            transform: 'translateX(70px)' // Push slightly right
                        }} 
                        formatter={renderLegendText} 
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
});

// --- TRIAL DONUT COMPONENT ---
const TrialDonut = ({ value, maxValue, label, size = 110 }) => {
    const safeValue = Math.max(0, Number(value) || 0);
    const safeMax = Math.max(1, Number(maxValue) || 1);
    const percent = Math.min(100, (safeValue / safeMax) * 100);

    let color = '#d32f2f';
    if (percent >= 100) color = '#4CAF50';
    else if (percent >= 83) color = '#8BC34A';
    else if (percent >= 66) color = '#BBC34a';
    else if (percent >= 50) color = '#FFC107';
    else if (percent >= 33) color = '#FF9800';
    else if (percent >= 16) color = '#d32f2f';

    const radius = size / 2;
    const stroke = size / 13.33; 
    const normalizedRadius = radius - stroke;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (percent / 100) * circumference;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div style={{ position: 'relative', width: size, height: size }}>
                <svg height={size} width={size} style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
                    <circle stroke="rgba(255,255,255,0.1)" fill="transparent" strokeWidth={stroke} r={normalizedRadius} cx={radius} cy={radius} />
                    <circle
                        stroke={color} fill="transparent" strokeWidth={stroke}
                        strokeDasharray={`${circumference} ${circumference}`}
                        style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease-in-out' }}
                        r={normalizedRadius} cx={radius} cy={radius} strokeLinecap="butt"
                    />
                </svg>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography 
                        variant="h6" 
                        component="span" // span has 0 default margin and satisfies Axe
                        sx={{ 
                            fontWeight: 'bold', 
                            color: 'white',
                            lineHeight: 1,
                            margin: 0,
                            display: 'block' // Ensures it respects the centering container
                        }}
                    >
                        {Math.round(safeValue)}
                    </Typography>
                </div>
            </div>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>{label}</Typography>
        </div>
    );
};


const BillingPage = () => {
    const { t, i18n } = useTranslation();
    const { openDocViewer, setIsFaqModalOpen } = useContext(UIContext);
    const { fetchWithAuth, user, setUser } = useContext(AuthContext);
    
    const [selectedCurrency, setSelectedCurrency] = useState(user?.preferred_currency || 'USD');

    const isMetered = !!user?.stripe_subscription_item_id;
    // Unbilled Balance State
    const [showUnbilled, setShowUnbilled] = useState(false);

    // --- FILTER STATES ---
    // Default Activity and ContentType to contain ALL options (Checked by default)
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        activity: ACTIVITY_OPTIONS,
        contentType: CONTENT_TYPE_OPTIONS
    });

    // UI State
    const [viewType, setViewType] = useState('cost'); 
    const [timeFrame, setTimeFrame] = useState('daily'); 
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [activeLabel, setActiveLabel] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
    const [paymentAlert, setPaymentAlert] = useState(null);
    const [costOffset, setCostOffset] = useState(0);
    const [isNoticeVisible, setIsNoticeVisible] = useState(true);

    // TanStack Query for Main Billing Data & Pagination
    const {
        data: billingPages,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading: initialLoading,
        isFetching: isTableRefreshing,
    } = useInfiniteQuery({
        queryKey: ['billing', filters], // Automatically refetches when filters change!
        queryFn: async ({ pageParam = 0 }) => {
            const queryParams = new URLSearchParams({
                limit: PAGE_SIZE,
                offset: pageParam,
                startDate: filters.startDate,
                endDate: filters.endDate,
                eventTypes: filters.activity.join(','),
                contentTypes: filters.contentType.join(',')
            });
            const response = await fetchWithAuth(`/api/user/billing?${queryParams.toString()}`);
            if (!response.ok) throw new Error('Failed to fetch billing data');
            return response.json();
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.has_more ? allPages.length * PAGE_SIZE : undefined;
        }
    });

    // TanStack Query for Unbilled Amount
    const { data: unbilledData, isFetching: isFetchingUnbilled } = useQuery({
        queryKey: ['unbilledAmount'],
        queryFn: async () => {
            const response = await fetchWithAuth('/api/billing/unbilled-status');
            if (!response.ok) throw new Error('Failed to fetch unbilled status');
            return response.json();
        },
        // Only fetch if the user is a paying customer AND has toggled the switch ON
        enabled: !!(isMetered && showUnbilled),
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes so toggling is instant
    });

    // Derive flattened data for your existing UI components
    const unbilledAmount = unbilledData?.unbilled_amount_usd ?? null;
    const historyRows = useMemo(() => billingPages?.pages.flatMap(page => page.history) || [], [billingPages]);
    const billingData = useMemo(() => {
        if (!billingPages?.pages?.length) return { accrued_units: 0, accrued_amount: 0, chart_data: [], currency_rates: { USD: 1 } };
        const firstPage = billingPages.pages[0]; // Chart data and rates come from the first page payload
        return {
            accrued_units: firstPage.accrued_units,
            accrued_amount: firstPage.accrued_amount,
            chart_data: firstPage.chart_data,
            currency_rates: firstPage.currency_rates || { USD: 1 }
        };
    }, [billingPages]);

    const [isOpeningPortal, setIsOpeningPortal] = useState(false);

    const handleManageBilling = async () => {
        setIsOpeningPortal(true);
        try {
            const response = await fetchWithAuth('/api/billing/customer-portal', { 
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ returnUrl: window.location.origin })
            });
            const data = await response.json();
            if (!response.ok) {
                // Use t() here to translate the error string/key from backend
                throw new Error(t(data.error, { defaultValue: t('billingPage.errors.generalError') }));
            }
            window.location.assign(data.url); // Redirect to portal
        } catch (err) {
            console.error("Portal Error:", err);
            setPaymentAlert({ type: 'error', message: err.message });
            setIsOpeningPortal(false);
        }
    };

    // Get today's date string for max/min constraints
    const todayString = new Date().toISOString().split('T')[0];

    // --- Calculate 6 months ago for min constraints ---
    const sixMonthsAgoDate = new Date();
    sixMonthsAgoDate.setMonth(sixMonthsAgoDate.getMonth() - 6);
    const minDateString = sixMonthsAgoDate.toISOString().split('T')[0];

    // =========================================================================
    // --- TRIAL CALCULATIONS (CONSOLIDATED & BULLETPROOFED) ---
    // =========================================================================
    const isCancellationPending = !!user?.is_cancellation_pending;
    
    // Helper to safely parse Postgres dates
    const parseDateSafe = (dateString, fallbackDaysAdd = 0) => {
        if (!dateString) {
            const d = new Date();
            d.setDate(d.getDate() + fallbackDaysAdd);
            return d;
        }

        // 1. Try standard native parsing first (Handles "Wed, 13 May 2026..." perfectly)
        let parsed = new Date(dateString);
        if (!isNaN(parsed.getTime())) return parsed;

        // 2. Fallback: If it fails, check if it's a raw SQL string "YYYY-MM-DD HH:MM:SS"
        if (typeof dateString === 'string') {
            // Only replace the space between the Date and Time portions
            const safeString = dateString.replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2}:\d{2})/, '$1T$2');
            parsed = new Date(safeString);
            if (!isNaN(parsed.getTime())) return parsed;
        }

        // 3. Absolute fallback to today to prevent app crashes
        return new Date();
    };

    const trialCreated = parseDateSafe(user?.trial_created_at || user?.created_at);
    // If missing entirely, assume 30 days from created
    const trialExpires = parseDateSafe(user?.trial_expires_at, 30); 
    const now = new Date();
    
    const totalDays = Math.max(1, Math.ceil((trialExpires - trialCreated) / (1000 * 60 * 60 * 24)));
    const daysRemaining = Math.max(0, Math.ceil((trialExpires - now) / (1000 * 60 * 60 * 24)));

    const totalGrantedUnits = Number(user?.compute_units_balance || 5000);
    // Uses the new trial_spent from backend SQL query
    const trialSpent = Number(user?.trial_spent || 0); 
    const unitsRemaining = Math.max(0, totalGrantedUnits - trialSpent);

    // --- Define if the user is currently enjoying an active trial, or in monthly mode ---
    const hasEverActivatedBilling = !!user?.billing_activated_at;
    const isTrialActive = !isMetered && !hasEverActivatedBilling && daysRemaining > 0 && unitsRemaining > 0;
    const isMonthlyView = isMetered || !isTrialActive;

    // --- 7-Day Trial Hidden Notice ---
    // 1. Extract the raw string or date from the user object
    const rawActivationDate = user?.billing_activated_at;
    
    // 2. Parse it safely
    const billingActivatedAt = rawActivationDate ? parseDateSafe(rawActivationDate) : null;
    
    let showTrialHiddenNotice = false;
    
    if (billingActivatedAt && !isNaN(billingActivatedAt.getTime())) {
        const now = new Date();
        const diffInMs = now - billingActivatedAt;
        const daysSinceActivation = diffInMs / (1000 * 60 * 60 * 24);
        
        // Check if it was within the last 7 days
        // (Note: we allow a tiny negative drift for server clock sync issues)
        showTrialHiddenNotice = daysSinceActivation >= -0.01 && daysSinceActivation <= 7;
    }
    
    // --- Helper: Format Currency ---
    const formatCurrency = useCallback((amount, currencyCode, decimals = 2) => {
        const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
        const numberPart = new Intl.NumberFormat('en-US', {
            style: 'decimal',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(amount);
        return `${symbol} ${numberPart}`;
    }, []);

    // --- Helper: Translate Backend Keys for Table ---
    const getTranslatedLabel = (backendKey) => {
        if (!backendKey) return 'N/A';
        // Ensure backendKey is lower case to match translation.json keys (e.g., 'analysis_new')
        return t(`billingPage.labels.${backendKey.toLowerCase()}`, { defaultValue: backendKey });
    };

    // --- Helper: Translate Chart Keys (Legend/Tooltip) ---
    // The chart data uses English Title Case keys (e.g. "Analysis New") to match COLOR_MAP.
    // This function converts them back to translation keys for display.
    const translateChartKey = (englishKey) => {
        const translationKey = englishKey.toLowerCase().replace(/ /g, '_');
        return t(`billingPage.labels.${translationKey}`, { defaultValue: englishKey });
    };

    // --- Helper: Format Date with Locale ---
    const formatDate = useCallback((dateStr, timeFrame) => {
        let parseString = dateStr;
        if (timeFrame === 'monthly' && dateStr.length === 7) {
            parseString += '-01'; 
        }
        const date = new Date(parseString);
        const lang = i18n.language; // Use current language
        
        if (timeFrame === 'monthly') {
            return date.toLocaleDateString(lang, { month: 'short', year: 'numeric' });
        }
        // Daily/Weekly
        return date.toLocaleDateString(lang, { month: 'short', day: 'numeric', year: '2-digit' });
    }, [i18n.language]);

    // --- Helper: Get Task Label ---
    const getTaskLabel = (taskKey) => {
        if (!taskKey) return '';
        // Map backend task keys to translation keys
        return t(`billingPage.tasks.${taskKey}`, { defaultValue: taskKey });
    };

    const UsageTrendsXAxisTick = ({ x, y, payload, activeLabel }) => {
        const isActive = payload?.value === activeLabel;
        return (
            <text
                x={x}
                y={y + 10}
                textAnchor="middle"
                fill={isActive ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)'}
                fontSize={10}
            >
                {payload.value}
            </text>
        );
    };

    // Effect to sync with user changes
    useEffect(() => {
        if (user?.preferred_currency) {
            setSelectedCurrency(user.preferred_currency);
        }
    }, [user?.preferred_currency]);


    // Check URL parameters for Stripe success/cancel redirects
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        
        if (params.get('payment') === 'success') {
            setPaymentAlert({ type: 'success', message: t('billingPage.paymentSuccess') });
            // Set a timer to clear the alert after 12 seconds
            setTimeout(() => {
                setPaymentAlert(null);
            }, 12000); // 12000ms = 12 seconds
            
            // --- The Stripe Webhook Race Condition ---
            // The browser redirects faster than Stripe's servers can hit our webhook.
            // We poll the backend a few times over ~6 seconds to give the DB time to update.
            let attempts = 0;
            const maxAttempts = 4;
            
            const pollUserDetails = async () => {
                attempts++;
                try {
                    const response = await fetchWithAuth('/api/user/me');
                    if (response.ok) {
                        const freshUser = await response.json();
                        // If the webhook has finished, this ID will now be present
                        if (freshUser.stripe_subscription_item_id) {
                            setUser(freshUser); // Updates the global UI state
                            return; // Stop polling
                        }
                    }
                } catch (e) {
                    console.error("Polling user details failed:", e);
                }
                
                // If not found yet, and we have attempts left, wait 1.5s and try again
                if (attempts < maxAttempts) {
                    setTimeout(pollUserDetails, 1500);
                }
            };

            pollUserDetails();
            window.history.replaceState(null, '', window.location.pathname);
            
        } else if (params.get('payment') === 'cancelled') {
            // Also explicitly handle the cancellation route for better UX
            setPaymentAlert({ type: 'info', message: t('billingPage.paymentCancelled', { defaultValue: 'Billing setup was cancelled.' }) });
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, [t, fetchWithAuth, setUser]);

    // Handle the 30-second auto-hide for the transition notice
    useEffect(() => {
        if (showTrialHiddenNotice) {
            const timer = setTimeout(() => {
                setIsNoticeVisible(false);
            }, 30000); // 30,000ms = 30 seconds

            return () => clearTimeout(timer);
        }
    }, [showTrialHiddenNotice]);

    // Clean TanStack Load More Handler
    const handleLoadMore = () => {
        fetchNextPage();
    };

    // The function that calls your Flask backend
    const handleActivateBilling = async () => {
        setIsRedirectingToStripe(true);
        try {
            const response = await fetchWithAuth('/api/billing/create-checkout-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ returnUrl: window.location.origin })
            });
            const data = await response.json();
            
            if (!response.ok) throw new Error(data.error || "Failed to initiate checkout");
            
            // Redirect the user to the secure Stripe-hosted page in the SAME TAB
            window.location.assign(data.url); // Use .assign() instead of .href
            
        } catch (err) {
            console.error("Stripe Redirect Error:", err);
            setPaymentAlert({ type: 'error', message: err.message });
            setIsRedirectingToStripe(false); // Only stop spinner if it fails
        }
    };

    // --- FILTER HANDLERS ---
    // Checks if any filter deviates from the default (All selected, no dates)
    const areFiltersActive = useMemo(() => {
        const isDateSet = filters.startDate !== '' || filters.endDate !== '';
        const isActivityFiltered = filters.activity.length !== ACTIVITY_OPTIONS.length;
        const isContentFiltered = filters.contentType.length !== CONTENT_TYPE_OPTIONS.length;
        return isDateSet || isActivityFiltered || isContentFiltered;
    }, [filters]);


    const handleResetFilters = () => {
        setFilters({ 
            startDate: '', 
            endDate: '', 
            activity: ACTIVITY_OPTIONS, 
            contentType: CONTENT_TYPE_OPTIONS 
        });
        // No need to close drawer here if we want instant feedback, 
        // but typically users might expect it to stay open to see changes 
        // OR close. License/History keeps it open. Let's keep it open.
    };

    const handleResetSingleFilter = (key) => {
        let defaultVal;
        // For multi-selects, default is ALL options
        if (key === 'activity') defaultVal = ACTIVITY_OPTIONS;
        else if (key === 'contentType') defaultVal = CONTENT_TYPE_OPTIONS;
        else defaultVal = ''; // For dates, default is empty
        setFilters(prev => ({ ...prev, [key]: defaultVal }));
    };

    const handleMultiSelectChange = (event) => {
        const { name, value } = event.target;
        // On autofill/clear, value might be string, handle gracefully
        const newValue = typeof value === 'string' ? value.split(',') : value;
        setFilters(prev => ({
            ...prev,
            [name]: newValue
        }));
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleViewToggle = (event, newView) => {
        if (newView !== null) setViewType(newView);
    };

    const handleTimeFrameToggle = (event, newFrame) => {
        if (newFrame !== null) setTimeFrame(newFrame);
    };

    const handleExportCSV = async () => {
        setIsExporting(true);
        try {
            // Get current rate for selected currency
            const rate = billingData.currency_rates[selectedCurrency] || 1.0;

            const queryParams = new URLSearchParams({
                startDate: filters.startDate,
                endDate: filters.endDate,
                eventTypes: filters.activity.join(','),
                contentTypes: filters.contentType.join(','),
                lang: i18n.language,
                currency: selectedCurrency,
                rate: rate
            });

            const response = await fetchWithAuth(`/api/user/billing/export?${queryParams.toString()}`);
            
            if (!response.ok) throw new Error("Export failed");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `usage_report_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (err) {
            console.error("Export failed:", err);
        } finally {
            setIsExporting(false);
        }
    };

    // Calculates cost based on units, converts to selected currency
    const calculateCost = useCallback((units) => {
        const costInUSD = units * COST_UNIT_PRICE_USD;
        const rate = billingData.currency_rates[selectedCurrency] || 1;
        return costInUSD * rate;
    }, [billingData.currency_rates, selectedCurrency]);

    // --- LOGIC: Exchange Rate Text ---
    const exchangeRateText = useMemo(() => {
        const rate = billingData.currency_rates[selectedCurrency] || 1;
        const convertedValue = 1.00 * rate;
        // Use formatter with 2 decimals
        const formattedVal = formatCurrency(convertedValue, selectedCurrency, 2);
        return `${t('billingPage.exchangeRateLabel')} ${formattedVal}`;
    }, [selectedCurrency, billingData.currency_rates, t, formatCurrency]);

    // --- CHART DATA PROCESSING ---
    const chartDataByView = useMemo(() => {
        if (!billingData.chart_data || !billingData.chart_data.length) return { platform: [], activation: [], content: [] };

        const platformMap = {};
        const activationMap = {};
        const contentMap = {};

        const now = new Date();
        const trialStart = new Date(trialCreated.getFullYear(), trialCreated.getMonth(), trialCreated.getDate());
        const trialEnd = new Date(trialExpires.getFullYear(), trialExpires.getMonth(), trialExpires.getDate());

        // Calculate the target month based on the arrow offset
        const targetMonthDate = new Date(now.getFullYear(), now.getMonth() + costOffset, 1);
        const targetMonth = targetMonthDate.getMonth();
        const targetYear = targetMonthDate.getFullYear();

        billingData.chart_data.forEach(item => {
            // Safely parse "YYYY-MM-DD" to avoid timezone offset bugs
            const [year, month, day] = item.usage_date.split('-');
            const rowDate = new Date(Number(year), Number(month) - 1, Number(day));

            // --- NEW DYNAMIC FILTERING LOGIC ---
            if (isTrialActive) {
                // If on active trial: Include ALL data within the entire trial start/end range
                if (rowDate < trialStart || rowDate > trialEnd) return;
            } else {
                // If metered OR expired trial: Only include data for the specific navigated month
                if (rowDate.getMonth() !== targetMonth || rowDate.getFullYear() !== targetYear) return;
            }

            const units = Number(item.total_units);
            const key = item.event_type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            
            // By Content Type View
            const contentKey = item.content_type ? item.content_type.charAt(0).toUpperCase() + item.content_type.slice(1) : 'System';
            contentMap[contentKey] = (contentMap[contentKey] || 0) + units;

            // By Activity View
            if (PLATFORM_KEYS.includes(key)) {
                platformMap[key] = (platformMap[key] || 0) + units;
            } else if (ACTIVATION_KEYS.includes(key)) {
                activationMap[key] = (activationMap[key] || 0) + units;
            }
        });

        const toArray = (map, keys) => {
            return keys
                .filter(key => map[key] !== undefined)
                .map(key => ({ name: key, value: map[key] }));
        };

        const sortedContent = Object.keys(contentMap)
            .map(name => ({ name, value: contentMap[name] }))
            .sort((a, b) => sortChartKeys(a.name, b.name));

        return {
            platform: toArray(platformMap, PLATFORM_KEYS),
            activation: toArray(activationMap, ACTIVATION_KEYS),
            content: sortedContent
        };
    }, [billingData.chart_data, trialCreated, trialExpires, costOffset, isTrialActive]);

    // --- BAR CHART DATA ---
    const barChartData = useMemo(() => {
        if (!billingData.chart_data) return { data: [], keys: [] };

        const rawData = billingData.chart_data; 
        const now = new Date();
        const maxCutoffDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // 1. Define the maximum lookback window
        if (timeFrame === 'daily') maxCutoffDate.setDate(maxCutoffDate.getDate() - 13); // 14 days (13 past + today)
        if (timeFrame === 'weekly') maxCutoffDate.setDate(maxCutoffDate.getDate() - (11 * 7)); // 12 weeks
        if (timeFrame === 'monthly') maxCutoffDate.setMonth(maxCutoffDate.getMonth() - 11); // 12 months

        // Align boundaries for clean periods
        if (timeFrame === 'monthly') maxCutoffDate.setDate(1);
        if (timeFrame === 'weekly') {
            const dayOfWeek = maxCutoffDate.getDay();
            maxCutoffDate.setDate(maxCutoffDate.getDate() - dayOfWeek);
        }

        // 2. Scan data to determine the "Smart" starting point
        let earliestDataDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let hasDataInWindow = false;
        let hasDataBeforeCutoff = false;

        rawData.forEach(row => {
            const [year, month, day] = row.usage_date.split('-');
            const rowDate = new Date(Number(year), Number(month) - 1, Number(day));
            
            if (rowDate < maxCutoffDate) {
                hasDataBeforeCutoff = true;
            } else if (rowDate < earliestDataDate) {
                earliestDataDate = new Date(rowDate);
                hasDataInWindow = true;
            }
        });

        // 3. Set actual chart start date (Smart Trimming)
        // - If data exists before the window: Show full max window
        // - If NO data exists at all: Show full max window (flat 0s)
        // - If data ONLY exists inside the window (e.g. starts 11th): Trim to the first data point
        let chartStartDate = new Date(
            (hasDataBeforeCutoff || !hasDataInWindow) ? maxCutoffDate : earliestDataDate
        );

        if (timeFrame === 'monthly') chartStartDate.setDate(1);
        if (timeFrame === 'weekly') {
            const dayOfWeek = chartStartDate.getDay();
            chartStartDate.setDate(chartStartDate.getDate() - dayOfWeek);
        }

        // 4. Pre-fill bins from chartStartDate up to TODAY (Ensures today always appears)
        const groupedMap = {};
        const allKeys = new Set();
        
        const getGroupKey = (d) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            
            if (timeFrame === 'monthly') return `${year}-${month}`;
            if (timeFrame === 'weekly') {
                const tempDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                tempDate.setDate(tempDate.getDate() - tempDate.getDay()); // Start of week
                const wYear = tempDate.getFullYear();
                const wMonth = String(tempDate.getMonth() + 1).padStart(2, '0');
                const wDay = String(tempDate.getDate()).padStart(2, '0');
                return `${wYear}-${wMonth}-${wDay}`;
            }
            return `${year}-${month}-${day}`;
        };

        let currentDate = new Date(chartStartDate);
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

        while (currentDate <= endOfToday) {
            const groupKey = getGroupKey(currentDate);
            if (!groupedMap[groupKey]) {
                groupedMap[groupKey] = { 
                    date: groupKey, 
                    displayDate: formatDate(groupKey, timeFrame) 
                };
            }
            
            // Safely increment period
            if (timeFrame === 'daily') currentDate.setDate(currentDate.getDate() + 1);
            else if (timeFrame === 'weekly') currentDate.setDate(currentDate.getDate() + 7);
            else if (timeFrame === 'monthly') currentDate.setMonth(currentDate.getMonth() + 1);
        }

        // Edge-case safeguard: Force "today" to exist in case timezone increment skips it
        const todayGroupKey = getGroupKey(now);
        if (!groupedMap[todayGroupKey]) {
            groupedMap[todayGroupKey] = {
                date: todayGroupKey,
                displayDate: formatDate(todayGroupKey, timeFrame)
            };
        }

        // 5. Populate pre-filled map with actual DB data
        rawData.forEach(row => {
            const [year, month, day] = row.usage_date.split('-');
            const rowDate = new Date(Number(year), Number(month) - 1, Number(day));
            const groupKey = getGroupKey(rowDate);

            // Only populate if it falls inside our mapped bins
            if (groupedMap[groupKey]) {
                let categoryLabel;
                if (viewType === 'content') {
                    categoryLabel = row.content_type ? row.content_type.charAt(0).toUpperCase() + row.content_type.slice(1) : 'System';
                } else {
                    categoryLabel = row.event_type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                }
                allKeys.add(categoryLabel);

                const cost = calculateCost(Number(row.total_units));
                groupedMap[groupKey][categoryLabel] = (groupedMap[groupKey][categoryLabel] || 0) + cost;
            }
        });

        const result = Object.values(groupedMap).sort((a, b) => new Date(a.date) - new Date(b.date));
        const sortedKeys = Array.from(allKeys).sort(sortChartKeys);
        
        return { data: result, keys: sortedKeys };

    }, [billingData.chart_data, timeFrame, viewType, calculateCost, formatDate]);

    // --- DYNAMIC Y-AXIS WIDTH CALCULATION ---
    const yAxisWidth = useMemo(() => {
        if (!barChartData || !barChartData.data || barChartData.data.length === 0) return 65;

        // 1. Find the highest stacked bar value in the chart
        let maxStackValue = 0;
        barChartData.data.forEach(item => {
            let dailySum = 0;
            barChartData.keys.forEach(key => {
                if (item[key]) dailySum += item[key];
            });
            if (dailySum > maxStackValue) maxStackValue = dailySum;
        });

        // 2. Return appropriate width based on the magnitude of the number
        if (maxStackValue >= 100000) return 75;  // e.g., $ 100,000
        if (maxStackValue >= 10000) return 65;   // e.g., $ 10,000
        if (maxStackValue >= 1000) return 55;    // e.g., $ 1,000
        return 45;                               // Default (e.g., $ 0.50 or $ 150)
    }, [barChartData]);

    // --- CUSTOM LEGEND FORMATTER ---
    const renderLegendText = (value, entry) => {
        // value is the English key from chart data (e.g. "Analysis New")
        // We translate it for display
        const translatedValue = translateChartKey(value);
        const definition = t(`billingPage.definitions.${value}`, { defaultValue: value });
        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#cbd5e1', fontSize: '0.75rem' }}>
                {/* 1. Info Icon (Moved BEFORE the text) */}
                <Tooltip title={definition} arrow placement="top">
                    <InfoOutlinedIcon 
                        sx={{ 
                            fontSize: '13px', 
                            color: 'rgba(255,255,255,0.4)', 
                            cursor: 'help',
                            '&:hover': { color: 'var(--secondary-color)' }
                        }} 
                    />
                </Tooltip>
                
                {/* 2. Text */}
                {translatedValue}
            </span>
        );
    };

    // --- UI: Custom Tooltip Components (Fixed Left Alignment) ---
    const BarChartTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const total = payload.reduce((acc, entry) => acc + entry.value, 0);
            const sortedPayload = [...payload].sort((a, b) => sortChartKeys(a.name, b.name));
            return (
                <div className="custom-chart-tooltip" style={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155', 
                    padding: '12px', 
                    borderRadius: '6px', 
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
                    textAlign: 'left'
                }}>
                    <p style={{ 
                        color: '#ffffff', 
                        fontWeight: 'bold', 
                        margin: '0 0 8px 0',
                        padding: '0 0 6px 0',
                        borderBottom: '1px solid #475569', 
                        fontSize: '0.9rem',
                        textAlign: 'left',
                        display: 'block',
                        width: '100%'
                    }}>
                        {label}
                    </p>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <tbody>
                            {sortedPayload.map((entry, index) => {
                                const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0.0';
                                // Translate the name here
                                const translatedName = translateChartKey(entry.name);
                                return (
                                    <tr key={index}>
                                        <td style={{ color: entry.color, fontSize: '0.8rem', fontWeight: '500', paddingRight: '12px', paddingBottom: '4px' }}>
                                            {translatedName}:
                                        </td>
                                        <td style={{ color: '#fff', fontSize: '0.8rem', fontWeight: '500', textAlign: 'right', paddingBottom: '4px' }}>
                                            {formatCurrency(entry.value, selectedCurrency, 3)}
                                        </td>
                                        <td style={{ color: 'var(--secondary-color)', fontSize: '0.75rem', textAlign: 'right', paddingLeft: '6px', paddingBottom: '4px' }}>
                                            ({percentage}%)
                                        </td>
                                    </tr>
                                );
                            })}
                            {/* Total Row - Inside the table for alignment */}
                            <tr>
                                <td colSpan={3} style={{ paddingTop: '4px' }}>
                                    <div style={{ borderTop: '1px solid #475569', marginTop: '4px' }}></div>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ color: '#94a3b8', fontSize: '0.8rem', paddingRight: '12px' }}>
                                    {t('billingPage.charts.total')}:
                                </td>
                                <td style={{ color: '#66bb6a', fontSize: '0.8rem', fontWeight: 'bold', textAlign: 'right' }}>
                                    {formatCurrency(total, selectedCurrency, 3)}
                                </td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        }
        return null;
    };

    const renderSortedLegend = ({ payload }) => {
        if (!payload) return null;
        const ordered = [...payload].sort((a, b) => sortChartKeys(a.value, b.value));
        return (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {ordered.map((entry) => {
                        // Translate and get definition here to keep JSX clean
                        const translatedValue = translateChartKey(entry.value);
                        const definition = t(`billingPage.definitions.${entry.value}`, { defaultValue: entry.value });

                        return (
                        <li key={entry.value} className="recharts-legend-item" style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                            {/* 1. Color Square */}
                            <svg width={10} height={10} style={{ marginRight: 8, flexShrink: 0 }}>
                                <rect width={10} height={10} fill={entry.color || getColor(entry.value)} rx={2} />
                            </svg>

                            {/* 2. Info Icon */}
                            <Tooltip title={definition} arrow placement="right">
                                <InfoOutlinedIcon 
                                    sx={{ 
                                        fontSize: '13px', 
                                        color: 'rgba(255,255,255,0.4)', 
                                        cursor: 'help',
                                        '&:hover': { color: 'var(--secondary-color)' }
                                    }} 
                                />
                            </Tooltip>
                            
                            {/* 3. Text */}
                            <span style={{ color: '#cbd5e1', fontSize: '0.75rem', marginLeft: 6, whiteSpace: 'nowrap' }}>
                                {translatedValue}
                            </span>


                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderHorizontalLegend = ({ payload }) => {
        if (!payload) return null;
        const ordered = [...payload].sort((a, b) => sortChartKeys(a.value, b.value));
        return (
            <ul style={{ listStyle: 'none', margin: 0, paddingTop: '20px', paddingBottom: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '24px' }}>
                {ordered.map((entry) => (
                    <li key={entry.value} className="recharts-legend-item" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {/* Colored Square */}
                        <svg width={14} height={14} style={{ flexShrink: 0 }}>
                            <rect width={14} height={14} fill={entry.color || getColor(entry.value)} rx={2} />
                        </svg>
                        
                        {/* Injects the Icon and Text */}
                        {renderLegendText(entry.value, entry)}
                    </li>
                ))}
            </ul>
        );
    };

    const renderContent = () => {
        if (initialLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
                    <CircularProgress color="secondary" />
                </Box>
            );
        }

        const platformData = chartDataByView.platform;
        const activationData = chartDataByView.activation;
        const contentData = chartDataByView.content;

        const totalPlatformUnits = platformData.reduce((acc, i) => acc + i.value, 0);
        const totalActivationUnits = activationData.reduce((acc, i) => acc + i.value, 0);
        const totalContentUnits = contentData.reduce((acc, i) => acc + i.value, 0);
        const globalTotalUnits = totalPlatformUnits + totalActivationUnits;

        // --- COLOR CALCULATION ---
        const daysPercent = Math.min(100, (daysRemaining / Math.max(1, totalDays)) * 100);

        let daysTextColor = '#d32f2f';
        if (daysPercent >= 100) daysTextColor = '#4CAF50';
        else if (daysPercent >= 83) daysTextColor = '#8BC34A';
        else if (daysPercent >= 66) daysTextColor = '#BBC34a';
        else if (daysPercent >= 50) daysTextColor = '#FFC107';
        else if (daysPercent >= 33) daysTextColor = '#FF9800';
        else if (daysPercent >= 16) daysTextColor = '#d32f2f';

        return (
            <>
                {/* --- 7-DAY INFO BANNER --- */}
                {/* This will show for 7 days regardless of whether they are active or pending cancellation */}
                {showTrialHiddenNotice && isNoticeVisible && isMetered && !isCancellationPending && (
                    <Alert 
                        severity="info" 
                        sx={{ 
                            mb: 4, 
                            backgroundColor: 'rgba(9, 132, 227, 0.1)', 
                            color: 'white', 
                            border: '1px solid rgba(9, 132, 227, 0.3)',
                            alignItems: 'center',
                            '& .MuiAlert-icon': { color: '#74b9ff' }
                        }}
                        // Optional: Add a close button so the user can dismiss it manually too
                        onClose={() => setIsNoticeVisible(false)}
                    >
                        {t('billingPage.trialHiddenNotice', { 
                            date: new Date(user.billing_activated_at).toLocaleDateString(i18n.language, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            })
                        })}
                    </Alert>
                )}
                {/* --- PAYMENT ALERTS & UPGRADE BANNER --- */}
                {paymentAlert && (
                    <Alert severity={paymentAlert.type} sx={{ mb: 3 }} onClose={() => setPaymentAlert(null)}>
                        {paymentAlert.message}
                    </Alert>
                )}

                {/* --- BILLING & SUBSCRIPTION MANAGEMENT AREA --- */}
                {isCancellationPending ? (
                    /* STATE: CANCELLATION PENDING */
                    <Paper 
                        className="glass-card-v2"
                        elevation={0} 
                        sx={{ 
                            p: { xs: 3, md: 4 }, 
                            mb: 4, 
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 4,
                            backgroundColor: 'transparent',
                            background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.15) 0%, rgba(0, 0, 0, 0.4) 100%)', 
                            borderStyle: 'solid',
                            borderColor: '#d32f2f',
                            borderWidth: '2px',
                            boxShadow: 'none', 
                            animation: 'pulse-red 2.5s infinite ease-in-out'
                        }}
                    >
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'center', md: 'flex-start' }, textAlign: { xs: 'center', md: 'left' } }}>
                            <Box>
                                <Typography variant="h5" component="h2" sx={{ color: '#ffcdd2', fontWeight: 'bold', mb: 1 }}>
                                    {t('billingPage.cancellationPendingTitle', 'Cancellation Pending')}
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                                    {t('billingPage.cancellationPendingDesc')}
                                </Typography>
                            </Box>
                            <Button 
                                variant="contained" 
                                color="error" 
                                size="large"
                                onClick={handleManageBilling}
                                disabled={isOpeningPortal}
                                sx={{ 
                                    whiteSpace: 'nowrap', 
                                    fontWeight: 'bold', 
                                    px: 4, 
                                    mt: 1,
                                    minWidth: '240px',
                                    '&.Mui-disabled': {
                                        backgroundColor: '#d32f2f',
                                        opacity: 0.7,
                                        color: 'white'
                                    }
                                }}
                            >
                                {isOpeningPortal ? <CircularProgress size={24} color="inherit" /> : t('billingPage.reactivateButton')}
                            </Button>
                        </Box>
                    </Paper>
                ) : !isMetered ? (
                    /* STATE: NOT SUBSCRIBED (Trial Mode or Expired) */
                   <Paper 
                        className="glass-card-v2"
                        elevation={0} 
                        sx={{ 
                            p: { xs: 3, md: 4 }, 
                            mb: 4, 
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 4,
                            backgroundColor: 'transparent',
                            background: (!isTrialActive)
                                ? 'linear-gradient(135deg, rgba(211, 47, 47, 0.15) 0%, rgba(0, 0, 0, 0.4) 100%)' 
                                : 'linear-gradient(135deg, rgba(9, 132, 227, 0.15) 0%, rgba(0, 0, 0, 0.3) 100%)', 
                            borderStyle: 'solid',
                            borderColor: (!isTrialActive) ? '#d32f2f' : 'rgba(255, 255, 255, 0.18)',
                            borderWidth: (!isTrialActive) ? '2px' : '1px',
                            boxShadow: (!isTrialActive) ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.2)', 
                            animation: (!isTrialActive) ? 'pulse-red 2.5s infinite ease-in-out' : 'none'
                        }}
                    >
                        {/* LEFT SIDE: Text and Button */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'center', md: 'flex-start' }, textAlign: { xs: 'center', md: 'left' } }}>
                            <Box>
                                <Typography variant="h5" component="h2" sx={{ color: (!isTrialActive) ? '#ffcdd2' : 'white', fontWeight: 'bold', mb: 1 }}>
                                    {(() => {
                                        if (isTrialActive) return t('billingPage.activateBillingTitle');
                                        
                                        // --- NEW: Identify cancelled/inactive paying customers ---
                                        if (hasEverActivatedBilling) return t('billingPage.subscriptionInactiveTitle', { defaultValue: 'Subscription Inactive' });
                                        
                                        // Priority 1: If days are 0 (even if units are also 0), show Expiration
                                        if (daysRemaining <= 0) return t('billingPage.trialExpiredTitle');
                                        
                                        // Priority 2: If days remain, but units are 0, show Exhausted
                                        if (unitsRemaining <= 0) return t('billingPage.trialUnitsExhaustedTitle');
                                        
                                        return t('billingPage.trialExpiredTitle'); // Fallback
                                    })()}
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                                    {/* --- Better description for inactive customers --- */}
                                    {hasEverActivatedBilling && !isMetered
                                        ? t('billingPage.subscriptionInactiveDesc', { defaultValue: 'Your billing account is currently inactive. Please add a valid payment method to resume unlimited access.' })
                                        : t('billingPage.activateBillingDesc')
                                    }
                                </Typography>
                            </Box>
                            <Button 
                                variant="contained" 
                                color={(!isTrialActive) ? "error" : "primary"} 
                                size="large"
                                onClick={handleActivateBilling}
                                disabled={isRedirectingToStripe}
                                sx={{ 
                                    whiteSpace: 'nowrap', 
                                    fontWeight: 'bold', 
                                    px: 4, 
                                    mt: 1,
                                    minWidth: '240px',      // Slightly wider to ensure it fits the longest translation
                                    height: '48px',        // Force a standard "Large Button" height
                                    display: 'flex',       // Use flex to center content
                                    alignItems: 'center',  // Vertically center the text/spinner
                                    justifyContent: 'center', // Horizontally center the text/spinner
                                    // Override the default disabled styles
                                    '&.Mui-disabled': {
                                        backgroundColor: (!isTrialActive) ? '#d32f2f' : 'var(--primary-color)',
                                        opacity: 0.7, // Just dim it slightly
                                        color: 'white'
                                    }
                                }}
                            >
                                {isRedirectingToStripe ? <CircularProgress size={24} color="inherit" /> : t('billingPage.addPaymentMethod')}
                            </Button>
                        </Box>

                        {/* MIDDLE: Vertical Divider (Hides on mobile) */}
                        <Box sx={{ 
                            width: '1px', 
                            height: '120px', 
                            backgroundColor: 'rgba(255,255,255,0.1)', 
                            display: { xs: 'none', md: 'block' } 
                        }} />

                        {/* RIGHT SIDE: Donuts */}
                        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
                            <TrialDonut value={unitsRemaining} maxValue={totalGrantedUnits} label={t('billingPage.unitsLeft')} />
                            <TrialDonut value={daysRemaining} maxValue={totalDays} label={t('billingPage.trialDaysLeft')} />
                        </Box>
                    </Paper>
                ) : (
                    /* STATE B: SUBSCRIBED (Manage Billing Mode) */
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                        <Button 
                            variant="outlined" 
                            color="secondary"
                            onClick={handleManageBilling}
                            disabled={isOpeningPortal}
                            className="load-more-button" 
                            sx={{ 
                                fontWeight: 'bold',
                                // 2. Remove the custom border logic that was causing the jump
                                // Only keep layout/font specific styling here
                                textTransform: 'uppercase', 
                                minWidth: '220px',
                                minHeight: '40px', // Forces the button to stay the same height
                                display: 'flex',   // Ensures content stays centered
                                alignItems: 'center',
                            }}
                        >
                            {isOpeningPortal ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }}/> : t('billingPage.manageBillingButton')}
                        </Button>
                    </Box>
                )}

                <Paper className="glass-card-v2 billing-summary-card" sx={{ p: 0, mb: 4, overflow: 'hidden' }}>
                    <Grid container>
                        {/* Donut Chart */}
                        <Grid item xs={12} md={8} sx={{ p: 3, borderRight: { md: '1px solid rgba(255,255,255,0.1)' }, borderBottom: { xs: '1px solid rgba(255,255,255,0.1)', md: 'none' } }}>
                            {/* --- NEW DYNAMIC HEADER & ARROWS --- */}
                            {(() => {
                                // Helper to generate "May 2026" strings
                                const getMonthYearString = (offset) => {
                                    const d = new Date();
                                    d.setMonth(d.getMonth() + offset);
                                    return d.toLocaleDateString(i18n.language, { month: 'short', year: 'numeric' });
                                };

                                const currentMonthYearStr = getMonthYearString(costOffset);
                                const prevMonthYearStr = getMonthYearString(costOffset - 1);
                                const nextMonthYearStr = getMonthYearString(costOffset + 1);

                                // Logic for arrows
                                const isPrevDisabled = costOffset <= -5; // Max 6 months back
                                const isNextDisabled = costOffset >= 0;  // Cannot go into the future

                                const titleText = isMonthlyView
                                    ? t('billingPage.costBreakdown.monthlyTitle', { monthYear: currentMonthYearStr, defaultValue: `Monthly Cost Breakdown - ${currentMonthYearStr}` })
                                    : t('billingPage.costBreakdown.trialTitle', { defaultValue: 'Trial Cost Breakdown' });

                                return (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 6 }}>
                                        <Box>
                                            <Typography variant="h6" component="h2" sx={{ color: 'white', fontWeight: 600 }}>
                                                {titleText}
                                            </Typography>
                                            
                                            {/* Only show arrows if the user is in Monthly View (Metered or Expired Trial) */}
                                            {isMonthlyView && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, ml: -1 }}>
                                                    <Tooltip title={isPrevDisabled ? '' : t('billingPage.costBreakdown.prevMonthTooltip', { monthYear: prevMonthYearStr, defaultValue: `Display ${prevMonthYearStr} data` })} arrow placement="bottom">
                                                        <span role="group">
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => setCostOffset(prev => prev - 1)} 
                                                                disabled={isPrevDisabled}
                                                                aria-label={t('billingPage.costBreakdown.prevMonthTooltip', { monthYear: prevMonthYearStr, defaultValue: `Display ${prevMonthYearStr} data` })}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}
                                                            >
                                                                <ChevronLeftIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={isNextDisabled ? '' : t('billingPage.costBreakdown.nextMonthTooltip', { monthYear: nextMonthYearStr, defaultValue: `Display ${nextMonthYearStr} data` })} arrow placement="bottom">
                                                        <span role="group">
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => setCostOffset(prev => prev + 1)} 
                                                                disabled={isNextDisabled}
                                                                aria-label={t('billingPage.costBreakdown.nextMonthTooltip', { monthYear: nextMonthYearStr, defaultValue: `Display ${nextMonthYearStr} data` })}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}
                                                            >
                                                                <ChevronRightIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </Box>
                                        
                                        <ToggleButtonGroup
                                            value={viewType}
                                            exclusive
                                            onChange={handleViewToggle}
                                            size="small"
                                            className="billing-toggle-group"
                                        >
                                            <ToggleButton value="cost">{t('billingPage.costBreakdown.byActivity')}</ToggleButton>
                                            <ToggleButton value="content">{t('billingPage.costBreakdown.byContentType')}</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                );
                            })()}
                            {/* --- END NEW DYNAMIC HEADER --- */}
                            
                            <Grid container spacing={2}>
                                {viewType === 'cost' ? (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            {platformData.length > 0 ? (
                                                <CustomDonut 
                                                    data={platformData} 
                                                    title={t('billingPage.costBreakdown.platformTitle')} 
                                                    totalUnits={totalPlatformUnits}
                                                    sharePercentage={globalTotalUnits > 0 ? (totalPlatformUnits / globalTotalUnits) * 100 : 0}
                                                    selectedCurrency={selectedCurrency}
                                                    calculateCost={calculateCost}
                                                    formatCurrency={formatCurrency}
                                                    getColor={getColor}
                                                    renderSortedLegend={renderSortedLegend}
                                                    renderLegendText={renderLegendText}
                                                    t={t}
                                                    translateChartKey={translateChartKey}
                                                />
                                            ) : (
                                                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 2 }}>
                                                    {t('billingPage.costBreakdown.platformTitle')}: {t('billingPage.charts.noData')}
                                                </Box>
                                            )}
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            {activationData.length > 0 ? (
                                                <CustomDonut 
                                                    data={activationData} 
                                                    title={t('billingPage.costBreakdown.activationTitle')} 
                                                    totalUnits={totalActivationUnits}
                                                    sharePercentage={globalTotalUnits > 0 ? (totalActivationUnits / globalTotalUnits) * 100 : 0}
                                                    selectedCurrency={selectedCurrency}
                                                    calculateCost={calculateCost}
                                                    formatCurrency={formatCurrency}
                                                    getColor={getColor}
                                                    renderSortedLegend={renderSortedLegend}
                                                    renderLegendText={renderLegendText}
                                                    t={t}
                                                    translateChartKey={translateChartKey}
                                                />
                                            ) : (
                                                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 2 }}>
                                                    {t('billingPage.costBreakdown.activationTitle')}: {t('billingPage.charts.noData')}
                                                </Box>
                                            )}
                                        </Grid>
                                    </>
                                ) : (
                                    <Grid item xs={12} sm={9} md={8} lg={7} sx={{ margin: '0 auto' }}>
                                        <CustomDonut 
                                            data={contentData} 
                                            title={t('billingPage.costBreakdown.byContentType')} 
                                            totalUnits={totalContentUnits} 
                                            // --- ADDED MISSING PROPS ---
                                            selectedCurrency={selectedCurrency}
                                            calculateCost={calculateCost}
                                            formatCurrency={formatCurrency}
                                            getColor={getColor}
                                            renderSortedLegend={renderSortedLegend}
                                            renderLegendText={renderLegendText}
                                            t={t}
                                            translateChartKey={translateChartKey}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        {/* Current Balance */}
                        <Grid item xs={12} md={4} sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                            <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title={t('billingPage.exchangeRateTooltip')} arrow placement="left">
                                    <InfoOutlinedIcon sx={{ fontSize: '16px', color: 'rgba(255,255,255,0.3)', cursor: 'help' }} />
                                </Tooltip>
                                <Select
                                    value={selectedCurrency}
                                    onChange={(e) => setSelectedCurrency(e.target.value)}
                                    variant="standard"
                                    disableUnderline
                                    inputProps={{ 'aria-label': t('billingPage.aria.selectCurrency') }}
                                    sx={{ 
                                        color: 'rgba(255,255,255,0.7)', 
                                        fontSize: '0.875rem',
                                        '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' }
                                    }}
                                >
                                    {Object.keys(billingData.currency_rates).map(curr => (
                                        // 1. ADDED SYMBOL TO DROPDOWN
                                        <MenuItem key={curr} value={curr}>
                                            {curr} ({CURRENCY_SYMBOLS[curr] || '$'})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            <Typography variant="subtitle2" component="p" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', marginTop: isMonthlyView ? '16px' : '32px' }}>
                                {isMonthlyView 
                                    ? (showUnbilled ? t('billingPage.labels.unbilledBalance', { defaultValue: 'Current Unbilled Balance' }) : t('billingPage.currentBalance')) 
                                    : t('billingPage.trialBalance')}
                            </Typography>

                            {/* --- SWITCHER - BETWEEN TITLE AND VALUE --- */}
                            {isMonthlyView && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: -3, mb: 1.5 }}>
                                    <Tooltip 
                                        title={showUnbilled 
                                            ? t('billingPage.tooltips.hideUnbilled', { defaultValue: 'Hide live unbilled balance' })
                                            : t('billingPage.tooltips.showUnbilled', { defaultValue: 'Check your live unbilled balance from Stripe' })
                                        } 
                                        arrow 
                                        placement="top" 
                                        PopperProps={{ className: 'global-dark-tooltip' }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    className="billing-custom-switch"
                                                    size="small"
                                                    checked={showUnbilled}
                                                    onChange={(e) => setShowUnbilled(e.target.checked)}
                                                    inputProps={{ 'aria-label': t('billingPage.aria.toggleUnbilled', { defaultValue: 'Toggle unbilled balance' }) }}
                                                />
                                            }
                                            label={t('billingPage.labels.showUnbilled', { defaultValue: 'Show unbilled' })}
                                            sx={{ 
                                                m: 0,
                                                alignItems: 'center',
                                                '& .MuiFormControlLabel-label': {
                                                    color: showUnbilled ? 'white' : 'rgba(255,255,255,0.6)', 
                                                    fontSize: '0.8rem', 
                                                    fontWeight: showUnbilled ? 600 : 500,
                                                    userSelect: 'none',
                                                    ml: 0.5
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                </Box>
                            )}
                            
                            <Typography variant="h2" sx={{ color: '#ffffff', fontWeight: 'bold', my: 1, textShadow: '0 0 15px rgba(255,255,255,0.3)' }}>
                                {isMonthlyView 
                                    ? (showUnbilled
                                        ? (isFetchingUnbilled || unbilledAmount === null 
                                            ? <CircularProgress size={40} sx={{ color: 'white', my: 1 }} /> 
                                            : formatCurrency(unbilledAmount * (billingData.currency_rates[selectedCurrency] || 1), selectedCurrency, 2))
                                        : formatCurrency(calculateCost(billingData.accrued_units), selectedCurrency, 2))
                                    : formatCurrency(calculateCost(unitsRemaining), selectedCurrency, 2)
                                }
                            </Typography>

                            {isMonthlyView && showUnbilled && unbilledAmount !== null && (
                                <Box sx={{ backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 2, p: 1.5, width: '100%', mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{t('billingPage.labels.totalMonthlyUsage', { defaultValue: 'Total Monthly Usage:' })}</Typography>
                                        <Typography variant="caption" sx={{ color: 'white' }}>{formatCurrency(calculateCost(billingData.accrued_units), selectedCurrency, 2)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{t('billingPage.labels.invoicedAmount', { defaultValue: 'Invoiced (Paid & Pending):' })}</Typography>
                                        <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                                            {formatCurrency(Math.max(0, calculateCost(billingData.accrued_units) - (unbilledAmount * (billingData.currency_rates[selectedCurrency] || 1))), selectedCurrency, 2)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>
                                {isMonthlyView
                                    ? t('billingPage.totalUnits', { count: Math.round(billingData.accrued_units).toLocaleString() })
                                    : t('billingPage.unitsRemaining', { count: Math.round(unitsRemaining).toLocaleString(), defaultValue: `${Math.round(unitsRemaining).toLocaleString()} Units Remaining` })
                                }
                            </Typography>


                            {/* --- TRIAL COUNTDOWN (ONLY SHOW IF ACTIVE) --- */}
                            {isTrialActive && (
                                <Typography variant="caption" sx={{ color: daysTextColor, fontWeight: 'bold', mb: 2 }}>
                                    {t('billingPage.trialDaysRemaining', { days: daysRemaining })}
                                </Typography>
                            )}
                            {/* 2. DYNAMIC EXCHANGE RATE TEXT */}
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mb: 3, fontSize: '0.75rem' }}>
                                {exchangeRateText}
                            </Typography>
                            <Button variant="outlined" size="small" startIcon={<HelpOutlineIcon sx={{ fontSize: '1rem !important' }} />} onClick={() => openDocViewer('business/billing_and_usage_units.md')} className="billing-help-btn">{t('billingPage.exploreUnits')}</Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Usage Trends */}
                <Paper className="glass-card-v2 billing-summary-card" sx={{ p: 3, mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" component="h2" sx={{ color: 'white', fontWeight: 600 }}>
                                {t('billingPage.usageTrends.title')} ({selectedCurrency})
                            </Typography>
                            <Tooltip 
                                title={
                                    timeFrame === 'daily' ? t('billingPage.tooltips.usageTrendsInfoDaily') :
                                    timeFrame === 'weekly' ? t('billingPage.tooltips.usageTrendsInfoWeekly') :
                                    t('billingPage.tooltips.usageTrendsInfoMonthly')
                                } 
                                arrow 
                                placement="right"
                            >
                                <InfoOutlinedIcon 
                                    sx={{ 
                                        fontSize: '18px', 
                                        color: 'rgba(255,255,255,0.4)', 
                                        cursor: 'help',
                                        '&:hover': { color: 'var(--secondary-color)' }
                                    }} 
                                />
                            </Tooltip>
                        </Box>
                        <ToggleButtonGroup
                            value={timeFrame}
                            exclusive
                            onChange={handleTimeFrameToggle}
                            size="small"
                            className="billing-toggle-group"
                        >
                            <ToggleButton value="daily">{t('billingPage.usageTrends.daily')}</ToggleButton>
                            <ToggleButton value="weekly">{t('billingPage.usageTrends.weekly')}</ToggleButton>
                            <ToggleButton value="monthly">{t('billingPage.usageTrends.monthly')}</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box className="usage-trends-container" sx={{ width: '100%', height: 300 }}>
                        {barChartData.data.length > 0 ? (
                            <ResponsiveContainer>
                                <BarChart
                                    data={barChartData.data}
                                    margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                                    onMouseMove={(state) => {
                                        if (state && state.isTooltipActive) {
                                            setActiveLabel(state.activeLabel ?? null);
                                        } else {
                                            setActiveLabel(null);
                                        }
                                    }}
                                    onMouseLeave={() => setActiveLabel(null)}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                                    <XAxis 
                                        dataKey="displayDate" 
                                        stroke="rgba(255,255,255,0.5)"
                                        interval={0}
                                        tick={(tickProps) => (
                                            <UsageTrendsXAxisTick {...tickProps} activeLabel={activeLabel} />
                                        )}
                                    />
                                    <YAxis 
                                        stroke="rgba(255,255,255,0.5)" 
                                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} 
                                        width={yAxisWidth}
                                        tickFormatter={(val) => formatCurrency(val, selectedCurrency, val > 10 ? 0 : 2)} 
                                    />
                                    <RechartsTooltip content={<BarChartTooltip />} cursor={false} />

                                    <Legend
                                        content={renderHorizontalLegend}
                                        verticalAlign="bottom"
                                        wrapperStyle={{ width: '100%', paddingTop: '5px', display: 'flex', justifyContent: 'center' }}
                                    />
                                    
                                    {barChartData.keys.map((key) => (
                                        <Bar 
                                            key={key} 
                                            dataKey={key} 
                                            stackId="a" 
                                            fill={getColor(key)} 
                                            barSize={timeFrame === 'monthly' ? 40 : 20}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.3)' }}>
                                {t('billingPage.charts.noData')}
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* --- ACTIVITY TABLE HEADER (With Filter Button) --- */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pr: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" component="h2" sx={{ color: 'white', fontWeight: 600 }}>
                            {t('billingPage.recentActivity')}
                        </Typography>
                        <Tooltip title={t('billingPage.tooltips.dateLimit')} arrow placement="right">
                            <InfoOutlinedIcon 
                                sx={{ 
                                    fontSize: '18px', 
                                    color: 'rgba(255,255,255,0.4)', 
                                    cursor: 'help', 
                                    '&:hover': { color: 'var(--secondary-color)' } 
                                }} 
                            />
                        </Tooltip>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* EXPORT BUTTON */}
                        <Tooltip title="Export filtered data as CSV" arrow>
                            <span>
                                <Button
                                    variant="outlined"
                                    color="secondary" // Matches the theme color of Load More
                                    size="small"
                                    onClick={handleExportCSV}
                                    disabled={isExporting}
                                    // 1. Apply the standard class
                                    className="load-more-button" 
                                    startIcon={isExporting ? <CircularProgress size={16} color="inherit"/> : <FileDownloadIcon />}
                                    sx={{
                                        // 2. Keep only functional layout/sizing overrides
                                        textTransform: 'uppercase', 
                                        minWidth: '130px', // Adjusted slightly to fit "Exporting..." text safely
                                        minHeight: '40px',
                                        fontWeight: 'bold',
                                        
                                        // 3. Remove hardcoded border-width logic to prevent jumping.
                                        // The .load-more-button class will handle the hover state correctly.
                                        '&.Mui-disabled': {
                                            borderColor: 'rgba(255, 255, 255, 0.1)',
                                            color: 'rgba(255, 255, 255, 0.3)',
                                            opacity: 0.5
                                        }
                                    }}
                                >
                                    {isExporting ? t('billingPage.export.exporting') : t('billingPage.export.button')}
                                </Button>
                            </span>
                        </Tooltip>
                        {areFiltersActive && (
                            <Button 
                                className="clear-filters-link-btn"
                                onClick={handleResetFilters}
                                variant="text"
                                endIcon={<FilterAltOffIcon />}
                                size="small"
                            >
                                {t('billingPage.buttons.resetFilters')}
                            </Button>
                        )}
                        <Tooltip title={t('billingPage.tooltips.showFilters')} arrow>
                            <IconButton 
                                className="filter-icon-btn" 
                                onClick={() => setIsFilterDrawerOpen(true)}
                                size="small"
                            >
                                <FilterAltIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                <TableContainer component={Paper} className="inner-dark-card batch-table-container">
                    <Table stickyHeader aria-label="billing history table">
                        <TableHead>
                            <TableRow>
                                {/* HEADER STYLES MOVED TO CSS: No inline bg colors here anymore */}
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('billingPage.table.date')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('billingPage.table.activity')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('billingPage.table.type')}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('billingPage.table.units')}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('billingPage.table.cost')} ({CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency})</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isTableRefreshing && !isFetchingNextPage && historyRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={24} color="secondary" />
                                    </TableCell>
                                </TableRow>
                            ) : historyRows.length > 0 ? (
                                historyRows.map((event, index) => (
                                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}>
                                        {/* Date, Activity, Type: All translated/formatted now */}
                                        <TableCell sx={{ color: 'white' }}>
                                            {/* Force UTC display to align with Backend Chart aggregation */}
                                            {new Date(event.created_at).toLocaleString(i18n.language, { 
                                                timeZone: 'UTC', 
                                                year: 'numeric', 
                                                month: 'short', 
                                                day: '2-digit', 
                                                hour: '2-digit', 
                                                minute: '2-digit', 
                                                hour12: false 
                                            }) + ' (UTC)'}
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', textTransform: 'capitalize' }}>
                                            {(() => {
                                                const activityLabel = getTranslatedLabel(event.event_type);
                                                const taskLabel = getTaskLabel(event.task);
                                                // If we have a task (e.g., 'ai_detection'), format as "Activity (Task)"
                                                return taskLabel 
                                                    ? `${activityLabel} (${taskLabel})` 
                                                    : activityLabel;
                                            })()}
                                        </TableCell>
                                        
                                        <TableCell sx={{ color: 'white', textTransform: 'capitalize' }}>
                                            {getTranslatedLabel(event.content_type)}
                                        </TableCell>
                                        
                                        <TableCell align="right" sx={{ color: 'white' }}>
                                            {Number(event.units_spent).toLocaleString(undefined, {
                                                minimumFractionDigits: 0, 
                                                maximumFractionDigits: 2
                                            })}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'var(--secondary-color)', fontWeight: '600' }}>
                                            {formatCurrency(calculateCost(event.units_spent), selectedCurrency, 3)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ color: 'rgba(255,255,255,0.4)', py: 4 }}>{t('billingPage.noData')}</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* --- LOAD MORE BUTTON --- */}
                {hasNextPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleLoadMore} 
                            disabled={isFetchingNextPage}
                            className="load-more-button"
                        >
                            {isFetchingNextPage ? <CircularProgress size={20} color="inherit" /> : t('billingPage.buttons.loadMore')}
                        </Button>
                    </Box>
                )}
            </>
        );
    };

    const descriptionText = isMetered 
        ? t('billingPage.description')
        : t('billingPage.trialDescription', { 
            start: trialCreated.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' }), 
            end: trialExpires.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' }) 
          });

    return (
        <div className={`history-page-wrapper ${isFilterDrawerOpen ? 'content-blurred-subtle' : ''}`}>
            <Helmet><title>{t('header.userMenu.billing')} - Vetra AI</title></Helmet>
            <main className="history-main-content">
                <Header showDataFlowPreview={false} showBackButton={true} />
                <div className="history-content-inner billing-content-inner">
                    <h1>{t('header.userMenu.billing')}</h1>
                    <p style={{ color: 'var(--secondary-color)', paddingLeft: 0, marginLeft: 0 }}>
                        {descriptionText}
                    </p>
                    {renderContent()}
                </div>
            </main>
            <Footer onOpenFaqModal={() => setIsFaqModalOpen(true)} showFaqButton={true} />
            {/* --- FILTER DRAWER --- */}
            <Drawer 
                anchor="left" 
                open={isFilterDrawerOpen} 
                onClose={() => setIsFilterDrawerOpen(false)} 
                PaperProps={{ 
                    sx: { 
                        backgroundColor: 'var(--tertiary-color)',
                        width: { xs: '100vw', sm: areFiltersActive ? 400 : 370 },
                        maxWidth: '100vw',
                        boxSizing: 'border-box',
                        p: 3,
                        overflowX: 'hidden',
                    } 
                }}
            >
                <div className="filter-drawer-container">
                    <header className="filter-drawer-header">
                        <Typography variant="h6" component="h2">{t('billingPage.filters.title')}</Typography>
                        <IconButton onClick={() => setIsFilterDrawerOpen(false)}><CloseIcon /></IconButton>
                    </header>
                    
                    <div className="filter-drawer-body">
                        {/* --- Activity Filter --- */}
                        <div className="filter-item-wrapper">
                            <FormControl className={`filter-form-control ${filters.activity.length !== ACTIVITY_OPTIONS.length ? 'active-filter' : ''}`}>
                                <InputLabel>{t('billingPage.costBreakdown.byActivity')}</InputLabel>
                                <Select
                                    multiple
                                    name="activity"
                                    value={filters.activity}
                                    onChange={handleMultiSelectChange}
                                    input={<OutlinedInput label={t('billingPage.costBreakdown.byActivity')} />}
                                    renderValue={(selected) => selected.length === ACTIVITY_OPTIONS.length ? t('billingPage.filters.allActivities') : selected.map(getTranslatedLabel).join(', ')}
                                    MenuProps={{ className: 'history-multi-select-menu' }}
                                >
                                    {ACTIVITY_OPTIONS.map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            <Checkbox checked={filters.activity.indexOf(opt) > -1} />
                                            <ListItemText primary={getTranslatedLabel(opt)} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {/* Show Reset if NOT ALL are selected */}
                            {filters.activity.length !== ACTIVITY_OPTIONS.length && (
                                <Tooltip title="Reset" arrow><IconButton className="single-reset-btn" onClick={() => handleResetSingleFilter('activity')} size="small"><ReplayIcon fontSize="small" /></IconButton></Tooltip>
                            )}
                        </div>

                        {/* --- Content Type Filter --- */}
                        <div className="filter-item-wrapper">
                            <FormControl className={`filter-form-control ${filters.contentType.length !== CONTENT_TYPE_OPTIONS.length ? 'active-filter' : ''}`}>
                                <InputLabel>{t('billingPage.table.type')}</InputLabel>
                                <Select
                                    multiple
                                    name="contentType"
                                    value={filters.contentType}
                                    onChange={handleMultiSelectChange}
                                    input={<OutlinedInput label={t('billingPage.table.type')} />}
                                    renderValue={(selected) => selected.length === CONTENT_TYPE_OPTIONS.length ? t('billingPage.filters.allContentTypes') : selected.map(getTranslatedLabel).join(', ')}
                                    MenuProps={{ className: 'history-multi-select-menu' }}
                                >
                                    {CONTENT_TYPE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            <Checkbox checked={filters.contentType.indexOf(opt) > -1} />
                                            <ListItemText primary={getTranslatedLabel(opt)} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {/* Show Reset if NOT ALL are selected */}
                            {filters.contentType.length !== CONTENT_TYPE_OPTIONS.length && (
                                <Tooltip title="Reset" arrow><IconButton className="single-reset-btn" onClick={() => handleResetSingleFilter('contentType')} size="small"><ReplayIcon fontSize="small" /></IconButton></Tooltip>
                            )}
                        </div>

                        {/* --- Date Range --- */}
                        <Box className="date-range-filter">
                            {/* FROM DATE */}
                            <div className={`filter-item-wrapper ${filters.startDate ? 'active-filter' : ''}`}>
                                {/* Added Tooltip Wrapper */}
                                <Tooltip title={t('billingPage.tooltips.dateLimit')} arrow placement="top">
                                    <TextField
                                        label={t('billingPage.filters.from')}
                                        name="startDate"
                                        type="date"
                                        value={filters.startDate}
                                        onChange={handleDateChange}
                                        InputLabelProps={{ shrink: true }}
                                        className="filter-text-field"
                                        fullWidth
                                        inputProps={{
                                            // The "To" date is the max. If not set, today is max.
                                            max: filters.endDate || todayString,
                                            // NEW: Restrict to last 6 months
                                            min: minDateString 
                                        }}
                                    />
                                </Tooltip>
                                {filters.startDate && (
                                    <Tooltip title="Reset" arrow>
                                        <IconButton className="single-reset-btn" onClick={() => handleResetSingleFilter('startDate')} size="small">
                                            <ReplayIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>

                            {/* TO DATE */}
                            <div className={`filter-item-wrapper ${filters.endDate ? 'active-filter' : ''}`}>
                                <TextField
                                    label={t('billingPage.filters.to')}
                                    name="endDate"
                                    type="date"
                                    value={filters.endDate}
                                    onChange={handleDateChange}
                                    InputLabelProps={{ shrink: true }}
                                    className="filter-text-field"
                                    fullWidth
                                    inputProps={{
                                        // The "From" date is the minimum. If not set, 6 months ago is min.
                                        min: filters.startDate || minDateString, 
                                        max: todayString
                                    }}
                                />
                                {filters.endDate && (
                                    <Tooltip title="Reset" arrow>
                                        <IconButton className="single-reset-btn" onClick={() => handleResetSingleFilter('endDate')} size="small">
                                            <ReplayIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        </Box>
                    </div>
                    <div className="reset-button-container" style={{flexDirection: 'column', gap: '10px'}}>
                    <div className="reset-button-container">
                        <Button variant="text" onClick={handleResetFilters} className="reset-filters-btn">
                            {t('billingPage.buttons.resetFilters')}
                        </Button>
                    </div>
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default BillingPage;